import {
  ArrowRight,
  CircleCheck,
  KeyRound,
  LoaderCircle,
  Mail,
  ShieldCheck,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { BrandMark } from "../components/BrandMark";
import { LandingControlDemo } from "../components/LandingControlDemo";
import type { AuthSession, RegistrationResponse } from "../types";

type LoginViewProps = {
  onLogin: (username: string, password: string) => Promise<AuthSession>;
  onRegister: (input: {
    workspaceName: string;
    displayName: string;
    username: string;
    email: string;
    password: string;
    acceptTerms: true;
    termsVersion: "2026-07-24";
    privacyVersion: "2026-07-24";
    website: "";
  }) => Promise<RegistrationResponse>;
  onVerifyEmail: (
    token: string,
  ) => Promise<{ status: "email_verified"; requestId: string }>;
  onResendVerification: (
    email: string,
  ) => Promise<{ status: "verification_requested"; requestId: string }>;
  onRequestPasswordReset: (
    email: string,
  ) => Promise<{ status: "password_reset_requested"; requestId: string }>;
  onConfirmPasswordReset: (
    token: string,
    newPassword: string,
  ) => Promise<{ status: "password_reset"; requestId: string }>;
};

type AuthMode =
  | "login"
  | "register"
  | "verification"
  | "verify-link"
  | "forgot"
  | "reset"
  | null;

export function LoginView({
  onConfirmPasswordReset,
  onLogin,
  onRegister,
  onRequestPasswordReset,
  onResendVerification,
  onVerifyEmail,
}: LoginViewProps) {
  const initialPath = window.location.pathname;
  const initialMode: AuthMode =
    initialPath === "/verify-email"
      ? "verify-link"
      : initialPath === "/reset-password"
        ? "reset"
        : null;
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  useEffect(() => {
    if (mode !== "verify-link" || !token) return;
    let active = true;
    setWorking(true);
    onVerifyEmail(token)
      .then(() => {
        if (!active) return;
        setNotice("邮箱已验证，现在可以登录 Qode。");
      })
      .catch((verifyError) => {
        if (!active) return;
        setError(
          verifyError instanceof Error
            ? verifyError.message
            : "邮箱验证未能完成。",
        );
      })
      .finally(() => {
        if (active) setWorking(false);
      });
    return () => {
      active = false;
    };
  }, [mode, onVerifyEmail, token]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setWorking(true);
    setError("");
    try {
      if (mode === "register") {
        const result = await onRegister({
          workspaceName,
          displayName,
          username,
          email,
          password,
          acceptTerms: true,
          termsVersion: "2026-07-24",
          privacyVersion: "2026-07-24",
          website: "",
        });
        setEmail(result.email);
        setNotice(
          result.mailStatus === "queued"
            ? "验证邮件已发送，请在 24 小时内完成验证。"
            : "Workspace 已创建，但邮件服务暂时不可用，请稍后重发。",
        );
        setMode("verification");
      } else if (mode === "forgot") {
        await onRequestPasswordReset(email);
        setNotice("如果该邮箱已绑定 Qode，重置邮件将很快送达。");
      } else if (mode === "reset") {
        if (!token) throw new Error("密码重置链接缺少令牌。");
        await onConfirmPasswordReset(token, newPassword);
        window.history.replaceState({}, "", "/");
        setPassword("");
        setNewPassword("");
        setNotice("密码已重置，请使用新密码登录。");
        setMode("login");
      } else {
        await onLogin(username, password);
      }
    } catch (authError) {
      setError(
        authError instanceof Error ? authError.message : "认证未能完成。",
      );
    } finally {
      setWorking(false);
    }
  };

  const openAuth = (nextMode: Exclude<AuthMode, null>) => {
    setMode(nextMode);
    setError("");
    setNotice("");
  };

  const resend = async () => {
    setWorking(true);
    setError("");
    try {
      await onResendVerification(email);
      setNotice("如果该邮箱仍待验证，一封新邮件将很快送达。");
    } catch (resendError) {
      setError(
        resendError instanceof Error
          ? resendError.message
          : "验证邮件未能重发。",
      );
    } finally {
      setWorking(false);
    }
  };

  const closeAuth = () => {
    if (mode === "verify-link" || mode === "reset") {
      window.history.replaceState({}, "", "/");
    }
    setMode(null);
    setError("");
    setNotice("");
  };

  const authCopy = {
    login: ["进入 Qode", "登录后规划、发布和治理公共二维码入口。"],
    register: [
      "创建 Qode Workspace",
      "注册后先验证邮箱，再以 Owner 身份进入独立 Workspace。",
    ],
    verification: ["检查你的邮箱", "验证链接有效期为 24 小时。"],
    "verify-link": [
      working ? "正在验证邮箱" : error ? "验证未完成" : "邮箱已验证",
      error ? "链接可能已过期，请重新申请验证邮件。" : "现在可以返回登录。",
    ],
    forgot: ["重置密码", "输入注册邮箱，我们会发送 30 分钟有效的重置链接。"],
    reset: ["设置新密码", "新密码生效后，其他已登录会话会全部退出。"],
  } as const;
  const [authTitle, authDescription] = mode
    ? authCopy[mode]
    : ["", ""];

  return (
    <main className="landing-page">
      <header className="landing-header">
        <div className="landing-brand">
          <BrandMark />
          <strong>Qode</strong>
        </div>
        <nav aria-label="访客导航">
          <button type="button" onClick={() => openAuth("login")}>
            登录
          </button>
          <button
            className="landing-register"
            type="button"
            onClick={() => openAuth("register")}
          >
            创建 Workspace
          </button>
        </nav>
      </header>

      <section className="landing-hero">
        <h1 className="landing-backdrop-title">Qode</h1>
        <div className="landing-hero-content">
          <p>
            让每一个二维码，成为持续运行的数字入口。
            <span>从创建、发布到监测与修复，都由 Qode Agent 接管。</span>
          </p>
          <div className="landing-actions">
            <button
              className="landing-primary"
              type="button"
              onClick={() => openAuth("register")}
            >
              创建 Workspace
              <ArrowRight size={17} />
            </button>
            <button
              className="landing-secondary"
              type="button"
              onClick={() => openAuth("login")}
            >
              登录控制台
            </button>
          </div>
        </div>
        <LandingControlDemo />
      </section>

      {mode ? (
        <div className="auth-overlay" role="presentation">
          <button
            className="auth-scrim"
            type="button"
            aria-label="关闭认证窗口"
            onClick={closeAuth}
          />
          <section className="auth-dialog" role="dialog" aria-modal="true">
            <button
              className="auth-close"
              type="button"
              aria-label="关闭"
              onClick={closeAuth}
            >
              <X size={18} />
            </button>
            <div className="login-intro">
              <span className="login-icon">
                {mode === "verification" ||
                mode === "verify-link" ||
                mode === "forgot" ? (
                  <Mail size={22} />
                ) : (
                  <ShieldCheck size={22} />
                )}
              </span>
              <h2>{authTitle}</h2>
              <p>{authDescription}</p>
            </div>
            {mode === "verification" ? (
              <div className="login-form auth-message">
                {email ? (
                  <div className="auth-email">{email}</div>
                ) : (
                  <label>
                    注册邮箱
                    <input
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  </label>
                )}
                {notice ? <div className="login-notice">{notice}</div> : null}
                {error ? <div className="login-error">{error}</div> : null}
                <button
                  className="primary-button"
                  type="button"
                  disabled={working || !email.trim()}
                  onClick={resend}
                >
                  {working ? (
                    <LoaderCircle className="spin" size={16} />
                  ) : (
                    <Mail size={16} />
                  )}
                  重发验证邮件
                </button>
                <button
                  className="auth-text-button"
                  type="button"
                  onClick={() => openAuth("login")}
                >
                  返回登录
                </button>
              </div>
            ) : mode === "verify-link" ? (
              <div className="login-form auth-message">
                {working ? (
                  <LoaderCircle className="spin auth-status-icon" size={24} />
                ) : !error ? (
                  <CircleCheck className="auth-status-icon" size={24} />
                ) : null}
                {notice ? <div className="login-notice">{notice}</div> : null}
                {error ? <div className="login-error">{error}</div> : null}
                {!working ? (
                  <button
                    className="primary-button"
                    type="button"
                    onClick={() => {
                      window.history.replaceState({}, "", "/");
                      openAuth("login");
                    }}
                  >
                    <ArrowRight size={16} />
                    返回登录
                  </button>
                ) : null}
              </div>
            ) : (
              <form className="login-form" onSubmit={submit}>
              {mode === "register" ? (
                <>
                  <label>
                    Workspace 名称
                  <input
                    autoComplete="organization"
                    required
                    value={workspaceName}
                    onChange={(event) => setWorkspaceName(event.target.value)}
                  />
                  </label>
                  <label>
                    显示名称
                  <input
                    autoComplete="name"
                    required
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                  />
                  </label>
                  <label>
                    邮箱
                    <input
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  </label>
                </>
              ) : null}
              {mode === "forgot" ? (
                <label>
                  注册邮箱
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </label>
              ) : null}
              {mode === "login" || mode === "register" ? (
                <label>
                  账号
                  <input
                    autoComplete="username"
                    required
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                  />
                </label>
              ) : null}
              {mode === "login" || mode === "register" ? (
                <label>
                  密码
                  <div className="password-input">
                    <KeyRound size={16} />
                    <input
                      type="password"
                      autoComplete={
                        mode === "register"
                          ? "new-password"
                          : "current-password"
                      }
                      minLength={mode === "register" ? 12 : 1}
                      required
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </div>
                </label>
              ) : null}
              {mode === "reset" ? (
                <label>
                  新密码
                  <div className="password-input">
                    <KeyRound size={16} />
                    <input
                      type="password"
                      autoComplete="new-password"
                      minLength={12}
                      required
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                    />
                  </div>
                </label>
              ) : null}
              {mode === "register" ? (
                <label className="registration-consent">
                  <input
                    type="checkbox"
                    checked={acceptedPolicies}
                    onChange={(event) =>
                      setAcceptedPolicies(event.target.checked)
                    }
                  />
                  <span>我已阅读并同意当前服务条款与隐私政策</span>
                </label>
              ) : null}
              {notice ? <div className="login-notice">{notice}</div> : null}
              {error ? <div className="login-error">{error}</div> : null}
              <button
                className="primary-button"
                type="submit"
                disabled={
                  working ||
                  ((mode === "login" || mode === "register") &&
                    (!username.trim() || !password)) ||
                  ((mode === "forgot" || mode === "register") &&
                    !email.trim()) ||
                  (mode === "reset" &&
                    (!token || newPassword.length < 12)) ||
                  (mode === "register" && password.length < 12) ||
                  (mode === "register" &&
                    (!workspaceName.trim() ||
                      !displayName.trim() ||
                      !acceptedPolicies))
                }
              >
                {working ? (
                  <LoaderCircle className="spin" size={16} />
                ) : (
                  <ArrowRight size={16} />
                )}
                {working
                  ? "正在处理"
                  : mode === "register"
                    ? "创建 Workspace"
                    : mode === "forgot"
                      ? "发送重置邮件"
                      : mode === "reset"
                        ? "确认重置"
                        : "登录"}
              </button>
              {mode === "login" ? (
                <div className="auth-secondary-actions">
                  <button
                    className="auth-text-button"
                    type="button"
                    onClick={() => openAuth("forgot")}
                  >
                    忘记密码
                  </button>
                  <button
                    className="auth-text-button"
                    type="button"
                    onClick={() => openAuth("verification")}
                  >
                    重发验证邮件
                  </button>
                </div>
              ) : null}
            </form>
            )}
          </section>
        </div>
      ) : null}
    </main>
  );
}
